import { useId, useState, type ReactNode } from 'react';

import styles from './Tooltip.module.css';

interface TooltipProps {
  /** Текст подсказки. Раскрывается по наведению **и по фокусу** (DS §5.5). */
  content: string;
  children: ReactNode;
  className?: string | undefined;
}

/**
 * Подсказка DESIGN_SYSTEM §5.10.
 *
 * Hover-only действий в продукте нет: подсказка открывается и с клавиатуры, а
 * её текст всегда присутствует в разметке через `aria-describedby` — скринридер
 * читает пояснение независимо от того, показан ли всплывающий блок.
 *
 * Подсказка **не может быть единственным носителем смысла**: раскрытие плашки
 * «Geprüft» поясняет формулировку, но не заменяет её.
 */
export function Tooltip({ content, children, className }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);

  return (
    <span
      className={[styles.wrapper, className].filter(Boolean).join(' ')}
      onMouseEnter={() => {
        setOpen(true);
      }}
      onMouseLeave={() => {
        setOpen(false);
      }}
      onFocus={() => {
        setOpen(true);
      }}
      onBlur={() => {
        setOpen(false);
      }}
    >
      <span aria-describedby={id} className={styles.trigger} tabIndex={0} role="button">
        {children}
      </span>
      <span
        id={id}
        role="tooltip"
        className={[styles.bubble, open && styles.bubbleOpen].filter(Boolean).join(' ')}
      >
        {content}
      </span>
    </span>
  );
}
